import { Component, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, ResolveData, Router } from '@angular/router';
import { MaterialToSupplier } from '@dave/types';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppConfirmationDialogComponent, AppConfirmationDialogData } from 'src/app/dave-utils-module/app-dialog-module/app-confirmation-dialog/app-confirmation-dialog.component';
import { FolderTypes } from '../../../dave-data-module/entities/folder.entity';
import { LedgerImportDocumentTypes } from '../../../dave-data-module/entities/ledger-import.entity';
import { CustomerResolver } from '../../../dave-data-module/guards/customer.resolver';
import { GeneratedDocumentsResolver } from '../../../dave-data-module/guards/generatedDocuments.resolver';
import { GeneratedDocumentsTypeResolver } from '../../../dave-data-module/guards/generatedDocumentsType.resolver';
import { MaterialGroupResolver } from '../../../dave-data-module/guards/material-group.resolver';
import { MaterialListResolver } from '../../../dave-data-module/guards/material-list.resolver';
import { TokenResolver } from '../../../dave-data-module/guards/token.resolver';
import { UserResolver } from '../../../dave-data-module/guards/user.resolver';
import { FolderDataService } from '../../../dave-data-module/services/folder-data.service';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { State } from '../../../dave-data-module/State';
import { DaveActions } from '../../../dave-data-module/State/actions/actions';
import { generatedDocumentsFeatureKey } from '../../../dave-data-module/State/reducers/generatedDocuments.reducer';
import { generatedDocumentsTypesFeatureKey } from '../../../dave-data-module/State/reducers/generatedDocumentsType.reducer';
import { MATERIAL_GROUP_KEY } from '../../../dave-data-module/State/reducers/material-group.reducers';
import { getCustomersFetched } from '../../../dave-data-module/State/selectors/customers.selectors';
import { getMaterialListsFetched } from '../../../dave-data-module/State/selectors/material-list.selector';
import { getUsersFetched } from '../../../dave-data-module/State/selectors/users.selectors';
import { CommissionMeta } from '../../../helper/page-metadata';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import { LoadingService } from '../../../services/loading.service';
import { WarehouseMovementComponent, WarehouseMovementComponentDialogData } from '../../../warehouse/warehouse-movement/warehouse-movement.component';
import { MaterialMainDataComponent } from '../material-main-data/material-main-data.component';
import {
    MaterialToSupplierListCardComponent
} from '../material-to-supplier-list-card/material-to-supplier-list-card.component';
import { MaterialAmountType } from '@dave/types/dist/proto/erp/warehouseMovements';

interface MaterialListTableData {
    Amount: number;
    CommissionName: string;
    Date: string;
    CreatedAt: Date;
    commissionId: number;
}

@Component({
    selector: 'app-material-detail-view',
    templateUrl: './material-detail-view.component.html',
    styleUrls: ['./material-detail-view.component.scss'],
})
export class MaterialDetailViewComponent {
    public static readonly RequiredResolvers: ResolveData = {
        token: TokenResolver,
        [MATERIAL_GROUP_KEY]: MaterialGroupResolver,
        [generatedDocumentsFeatureKey]: GeneratedDocumentsResolver,
        [generatedDocumentsTypesFeatureKey]: GeneratedDocumentsTypeResolver,
    };
    @ViewChild('materialData') materialData: MaterialMainDataComponent;
    @ViewChild(MaterialToSupplierListCardComponent) materialToSupplierListCardComponent!: MaterialToSupplierListCardComponent;
    public Editing = false;
    public CommissionMeta = CommissionMeta;

    public MaterialId$ = this.route.paramMap.pipe(
        map((paramMap) => paramMap.get('materialId')),
        map((id) => +id || null),
        distinctUntilChanged(),
        tap((id) => (this.Editing = !id)),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    public Material$ = this.MaterialId$.pipe(switchMap((id) => (id ? this.materialDataService.getMaterialById$(id) : of(null))));
    public fileExplorerLoading = new BehaviorSubject<boolean>(true);

    constructor(
        private actions$: Actions<DaveActions>,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store<State>,
        public LS: LoadingService,
        protected dialog: MatDialog,
        protected cls: CustomLabelService,
        private folderDataService: FolderDataService,
        private materialDataService: MaterialDataService,
        materialListResolver: MaterialListResolver,
        userResolver: UserResolver,
        customerResolver: CustomerResolver,
    ) {
        firstValueFrom(this.store.select(getMaterialListsFetched)).then((fetched) => {
            if (!fetched) {
                materialListResolver.resolve();
            }
        });

        firstValueFrom(this.store.select(getCustomersFetched)).then((fetched) => {
            if (!fetched) {
                customerResolver.resolve();
            }
        });
        firstValueFrom(this.store.select(getUsersFetched)).then((fetched) => {
            if (!fetched) {
                userResolver.resolve();
            }
        });
    }

    onSave() {
        this.materialData.SaveForm().then((material) => {
            this.LS.endLoading('saveMaterial');
            if (this.materialToSupplierListCardComponent) {
                this.materialToSupplierListCardComponent.refreshTable();
            }
            this.router.navigate(['../', material.Id], { relativeTo: this.route });
        });
        this.LS.startLoading('saveMaterial');
    }
    onUndo() {
        this.materialData.ResetForm();
        firstValueFrom(this.MaterialId$).then((id) => {
            if (id) {
                this.Editing = false;
            }
        });
    }
    async onDelete() {
        const materialId = await firstValueFrom(this.MaterialId$.pipe(map((id) => id)));
        const materialName = await firstValueFrom(this.Material$.pipe(map((m) => m.Name)));
        this.dialog
            .open<AppConfirmationDialogComponent, AppConfirmationDialogData>(AppConfirmationDialogComponent, {
                data: {
                    heading: 'Material wirklich löschen?',
                    paragraph: 'Sind sie sicher, das Sie das Material ' + materialName + ' löschen möchten?',
                    styleDelete: true,
                },
            })
            .afterClosed()
            .subscribe(([res]) => {
                if (res) {
                    this.LS.startLoading('deleteMaterial');
                    this.materialDataService.deleteMaterial({ Id: String(materialId) }).then(() => {
                        this.LS.endLoading('deleteMaterial');
                        this.materialData.ResetForm();
                        this.Editing = false;
                        this.router.navigate(['../'], { relativeTo: this.route });
                    });
                }
                return;
            });
    }
    protected readonly MaterialToSupplier = MaterialToSupplier;
    protected readonly LedgerImportDocumentTypes = LedgerImportDocumentTypes;

    async openWarehouseMovementDialog(type: 'in' | 'out') {
        const materialId = await firstValueFrom(this.MaterialId$);
        this.dialog.open<WarehouseMovementComponent, WarehouseMovementComponentDialogData>(WarehouseMovementComponent, {
            ...WarehouseMovementComponent.DefaultConfig,
            data: {
                defaultWarehouseMovementType: type,
                defaultAddedMaterials: [{materialId, warehouseMovementType: type === 'in' ? MaterialAmountType.MaterialAmountTypeInput : MaterialAmountType.MaterialAmountTypeOutput}],
            },
        });
    }
}
